import React, { useState, useRef } from 'react';
import { Instagram, Mail, MessageCircle, Check } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { useToast } from "@/hooks/use-toast";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    businessType: '',
    contact: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Replace these with your actual EmailJS service ID, template ID, and public key
    const serviceId = 'service_id'; // You'll need to create this in EmailJS
    const templateId = 'template_id'; // You'll need to create this in EmailJS
    const publicKey = 'public_key'; // You'll get this from EmailJS
    
    // Prepare the template parameters to match your EmailJS template variables
    const templateParams = {
      from_name: formData.name,
      business_type: formData.businessType,
      contact: formData.contact,
      message: formData.message,
      to_email: 'business@grow99x.com', // This will be used in your EmailJS template
    };

    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        console.log('Email sent successfully:', response);
        toast({
          title: "Message Sent!",
          description: "Thanks for your inquiry! We will get back to you within 24 hours.",
        });
        // Reset form
        setFormData({
          name: '',
          businessType: '',
          contact: '',
          message: ''
        });
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        toast({
          title: "Something went wrong",
          description: "Unable to send your message. Please try again later.",
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <section id="contact" className="section-container bg-white py-16">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left side with heading and contact methods */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-grow99x-text">
                Let's Grow Your Brand — Together
              </h2>
              <p className="text-xl text-gray-600 mt-4">
                We reply to every message. Seriously.
              </p>
            </div>
            
            {/* Contact buttons with enhanced hover effects */}
            <div className="space-y-4 mt-8">
              <a 
                href="https://wa.me/61469085544" 
                className="flex items-center p-4 bg-white text-grow99x-text rounded-lg w-full border border-gray-200 transition-all duration-300 hover:bg-[#25D366] hover:text-white hover:border-transparent"
              >
                <span className="p-2 rounded-full mr-3">
                  <MessageCircle size={24} className="text-grow99x-text group-hover:text-white transition-colors duration-300" />
                </span>
                Chat with Us on WhatsApp
              </a>
              
              <a 
                href="mailto:business@grow99x.com" 
                className="flex items-center p-4 bg-white text-grow99x-text rounded-lg w-full border border-gray-200 transition-all duration-300 hover:bg-[#4A90E2] hover:text-white hover:border-transparent"
              >
                <span className="p-2 mr-3">
                  <Mail size={24} className="text-grow99x-text group-hover:text-white transition-colors duration-300" />
                </span>
                Email business@grow99x.com
              </a>
              
              <a 
                href="https://www.instagram.com/grow99x/" 
                className="flex items-center p-4 bg-white text-grow99x-text rounded-lg w-full border border-gray-200 transition-all duration-300 hover:bg-gradient-to-r hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:text-white hover:border-transparent"
              >
                <span className="p-2 mr-3">
                  <Instagram size={24} className="text-grow99x-text group-hover:text-white transition-colors duration-300" />
                </span>
                DM Us on Instagram
              </a>
            </div>
          </div>
          
          {/* Right side with contact image - removed border and shadow */}
          <div className="flex items-center justify-center">
            <img 
              src="/lovable-uploads/ab8ea75e-b613-4c4a-addd-2773c6cb9a37.png" 
              alt="Team collaboration" 
              className="w-full max-w-md"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
